import React from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGithub,
  faLinkedin,
  faXTwitter,
  faInstagram,
  faWhatsapp,
} from "@fortawesome/free-brands-svg-icons";
import {
  faEnvelope,
  faLocationDot,
  faArrowRight,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

// Contact details and links
const EMAIL = "jatin@interestingplus.qzz.io";

const contactMethods = [
  {
    id: "email",
    icon: faEnvelope,
    title: "Email",
    value: EMAIL,
    helper: "Send me an email",
    link: `mailto:${EMAIL}`,
  },
  {
    id: "linkedin",
    icon: faLinkedin,
    title: "LinkedIn",
    value: "in/jatin-poriya",
    helper: "Let's connect",
    link: "https://www.linkedin.com/in/jatin-poriya/",
  },
  {
    id: "github",
    icon: faGithub,
    title: "GitHub",
    value: "interestingPlus",
    helper: "Explore my code",
    link: "https://github.com/interestingPlus/",
  },
  {
    id: "whatsapp",
    icon: faWhatsapp,
    title: "WhatsApp",
    value: "+91 72018 40095",
    helper: "Response within 24 hours",
    link: "https://wa.me/917201840095",
  },
  {
    id: "location",
    icon: faLocationDot,
    title: "Location",
    value: "Gujarat, India",
    helper: "Available worldwide 🌍",
    link: "https://maps.app.goo.gl/aD7zXMx3TW5zs1ZR9",
  },
];

// --- SUBTLE ORBITAL ILLUSTRATION ---
const OrbitalIllustration = () => {
  return (
    <div className="relative w-full h-[300px] md:h-[400px] flex items-center justify-center opacity-60">
      {/* Center Glow */}
      <div className="absolute w-20 h-20 bg-purple-500/20 rounded-full blur-xl animate-pulse"></div>
      <div className="absolute w-4 h-4 bg-purple-400 rounded-full shadow-[0_0_20px_rgba(168,85,247,0.8)]"></div>

      {/* Orbit 1 */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="absolute w-[150px] h-[150px] border border-white/10 rounded-full border-dashed"
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-blue-400 rounded-full shadow-[0_0_10px_rgba(96,165,250,0.8)]"></div>
      </motion.div>

      {/* Orbit 2 */}
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
        className="absolute w-[250px] h-[250px] border border-white/10 rounded-full border-dashed"
      >
        <div className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-purple-400 rounded-full shadow-[0_0_15px_rgba(168,85,247,0.8)]"></div>
        <div className="absolute bottom-1/4 left-0 -translate-x-1/2 w-1.5 h-1.5 bg-emerald-400 rounded-full shadow-[0_0_10px_rgba(52,211,153,0.8)]"></div>
      </motion.div>

      {/* Orbit 3 */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
        className="absolute w-[350px] h-[350px] border border-white/5 rounded-full border-dashed hidden md:block"
      >
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-pink-400 rounded-full shadow-[0_0_10px_rgba(244,114,182,0.8)]"></div>
      </motion.div>
    </div>
  );
};

const Contact = () => {
  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section className="relative min-h-screen text-slate-200 overflow-hidden font-sans border-t border-white/5 py-24 lg:py-32 flex flex-col justify-center">
      {/* BACKGROUND: Grid & Glow */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:32px_32px]"></div>
      <div className="absolute top-0 right-0 w-[50vw] h-[50vw] bg-purple-600/10 rounded-full blur-[150px] opacity-50 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[50vw] h-[50vw] bg-blue-600/10 rounded-full blur-[150px] opacity-40 pointer-events-none"></div>

      <div className="max-w-[1600px] mx-auto w-full px-6 md:px-12 lg:px-20 relative z-10 flex flex-col lg:flex-row gap-16 lg:gap-8">
        {/* LEFT SIDE: Cinematic Intro (55%) */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="w-full lg:w-[55%] flex flex-col justify-between"
        >
          <div>
            {/* <h2 className="text-[60px] sm:text-[80px] lg:text-[100px] font-thin leading-[0.9] tracking-tighter uppercase text-slate-200 mb-8">
              Let's Build <br />
              <span className="text-white font-bold">
                Something <br className="hidden sm:block" /> Together.
              </span>
            </h2> */}

            <h2 className="text-5xl md:text-7xl font-extrabold text-white mb-6 leading-tight">
              <span style={{ fontWeight: "400" }}>Let's Build</span>

              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">
                Something <br className="hidden sm:block" /> Together.
              </span>
            </h2>

            <p className="text-xl text-slate-400 max-w-lg leading-relaxed font-light">
              Whether it's a startup idea, a freelance project, a full-time
              opportunity, or simply discussing technology... I'd love to hear
              from you.
            </p>

            {/* Glowing Availability Badge */}
            <div className="mt-8 inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 backdrop-blur-md">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
              </span>
              <span className="text-emerald-400 text-sm font-bold tracking-wide">
                Open to Opportunities
              </span>
            </div>
          </div>

          {/* Animated Illustration */}
          <div className="mt-12 lg:mt-0 flex-1 flex flex-col justify-end">
            <OrbitalIllustration />
          </div>
        </motion.div>

        {/* RIGHT SIDE: Contact Methods & CTA (45%) */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="w-full lg:w-[45%] flex flex-col pt-4 lg:pt-0"
        >
          {/* Section Header */}
          <motion.div
            variants={itemVariants}
            className="flex items-center gap-3 mb-10"
          >
            <div className="h-px w-10 bg-purple-500"></div>
            <span className="text-purple-400 font-bold tracking-widest uppercase text-xs">
              Contact
            </span>
          </motion.div>

          <motion.h3
            variants={itemVariants}
            className="text-4xl sm:text-5xl font-bold text-white mb-10"
          >
            Get in Touch
          </motion.h3>

          {/* Contact cards */}
          <div className="flex flex-col gap-4 w-full">
            {contactMethods.map((method) => {
              const Wrapper = method.link ? "a" : "div";
              const isExternal = method.link?.startsWith("http");

              return (
                <motion.div key={method.id} variants={itemVariants}>
                  <Wrapper
                    href={method.link || undefined}
                    target={isExternal ? "_blank" : undefined}
                    rel={isExternal ? "noopener noreferrer" : undefined}
                    className={`group flex items-center justify-between gap-4 p-5 sm:p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-purple-500/30 transition-all duration-300 w-full overflow-hidden relative ${method.link ? "cursor-pointer" : "cursor-default"}`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-purple-500/5 to-transparent -translate-x-full group-hover:translate-x-0 transition-transform duration-700 ease-out z-0 pointer-events-none" />

                    <div className="flex items-center gap-5 relative z-10 min-w-0">
                      <div className="shrink-0 w-12 h-12 rounded-full bg-[#0f172a] border border-white/10 flex items-center justify-center text-slate-400 group-hover:text-purple-400 group-hover:scale-110 transition-all duration-300 shadow-[inset_0_0_10px_rgba(255,255,255,0.02)]">
                        <FontAwesomeIcon
                          icon={method.icon}
                          className="w-5 h-5"
                          fixedWidth
                        />
                      </div>

                      <div className="min-w-0">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                          {method.title}
                        </p>
                        <p className="text-sm sm:text-lg font-medium text-slate-200 group-hover:text-white transition-colors break-words">
                          {method.value}
                        </p>
                      </div>
                    </div>

                    <div className="hidden sm:flex shrink-0 items-center gap-2 text-sm text-slate-500 group-hover:text-purple-400 transition-colors relative z-10">
                      <span className="max-w-[130px] text-right">
                        {method.helper}
                      </span>
                      {method.link && (
                        <FontAwesomeIcon
                          icon={faArrowRight}
                          className="w-4 h-4 -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300"
                        />
                      )}
                    </div>
                  </Wrapper>
                </motion.div>
              );
            })}
          </div>

          {/* Premium CTA Panel */}
          <motion.div
            variants={itemVariants}
            className="mt-12 bg-gradient-to-br from-[#0f172a] to-[#020617] border border-purple-500/20 rounded-3xl p-8 sm:p-10 relative overflow-hidden group shadow-[0_0_40px_rgba(147,51,234,0.05)]"
          >
            {/* CTA Background Glow */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2 group-hover:bg-purple-500/20 transition-colors duration-700 pointer-events-none"></div>

            <div className="flex flex-col xl:flex-row items-start xl:items-center justify-between gap-8 relative z-10">
              <div className="flex-1">
                <h4 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-400" /> Have an
                  exciting project?
                </h4>
                <p className="text-slate-400 mb-4 text-sm sm:text-base">
                  Let's discuss your idea and build something meaningful
                  together.
                </p>
                <div className="inline-block px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-slate-300 font-mono tracking-wide">
                  <span className="text-purple-400">Flexible pricing</span>{" "}
                  starting from $299+
                </div>
              </div>

              <Link
                to="https://wa.me/917201840095?text=Hi%20Jatin!%20I%20visited%20your%20portfolio%20and%20I%27m%20interested%20in%20working%20with%20you.%20I%20have%20a%20project%20idea%20I%27d%20love%20to%20discuss.%20Let%27s%20connect!"
                target="_blank"
                rel="noopener noreferrer"
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full xl:w-auto whitespace-nowrap px-8 py-4 font-bold text-white rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 hover:opacity-90 transition-all duration-300 shadow-[0_0_20px_rgba(147,51,234,0.3)] hover:shadow-[0_0_30px_rgba(147,51,234,0.5)] flex items-center justify-center gap-3"
                >
                  Let's Discuss <ArrowRight className="w-5 h-5" />
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
